import { ArrowLeft, Plus, Download, Settings, AlertCircle, ToggleLeft, ToggleRight, Home, ChevronRight, ExternalLink, FileText, Globe, Rss, Building2, Flag } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { LawPackUpdatesDialog } from '@/components/LawPackUpdatesDialog';
import { QuickCreateLawPackWizard } from '@/components/QuickCreateLawPackWizard';

interface ExternalLink {
  title: string;
  url: string;
  type: 'regulation' | 'guidance' | 'update';
  description?: string;
}

interface SourceDocument {
  title: string;
  url: string;
  type: string;
  size?: string;
}

interface LawPack {
  id: string;
  name: string;
  version: string;
  region: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  critical_limits: any;
  sites_count?: number;
  official_website?: string;
  update_feed_url?: string;
  regulatory_authority?: string;
  external_links?: ExternalLink[];
  source_documents?: SourceDocument[];
}

const LawPacks = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [lawPacks, setLawPacks] = useState<LawPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [updatesDialogOpen, setUpdatesDialogOpen] = useState(false);
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);
  const [pendingUpdatesCount, setPendingUpdatesCount] = useState(0);
  const isMasterAdmin = user?.id === '7333de0f-e17e-4c3c-a6a5-5feafd753a99';

  useEffect(() => {
    if (user) {
      fetchLawPacks();
      fetchPendingUpdatesCount();
    }
  }, [user]);

  const fetchPendingUpdatesCount = async () => {
    if (!user) return;
    try {
      const { data: roleRow } = await supabase
        .from('user_roles')
        .select('tenant_id')
        .eq('user_id', user.id)
        .maybeSingle();
      const tid = roleRow?.tenant_id;
      const { data: all } = await supabase
        .from('law_pack_updates')
        .select('id');
      const allIds = (all ?? []).map(u => u.id);
      if (!tid || allIds.length === 0) {
        setPendingUpdatesCount(allIds.length);
        return;
      }
      const { data: states } = await supabase
        .from('tenant_law_pack_updates')
        .select('update_id')
        .eq('tenant_id', tid);
      const handled = new Set((states ?? []).map(s => s.update_id));
      setPendingUpdatesCount(allIds.filter(id => !handled.has(id)).length);
    } catch {
      setPendingUpdatesCount(0);
    }
  };

  const fetchLawPacks = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('law_packs')
        .select(`
          *,
          units!inner(count)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Process the data to include sites count and proper typing
      const processedData = data?.map(pack => ({
        ...pack,
        sites_count: pack.units?.length || 0,
        external_links: (pack.external_links as unknown as ExternalLink[]) || [],
        source_documents: (pack.source_documents as unknown as SourceDocument[]) || []
      })) || [];

      setLawPacks(processedData);
    } catch (error: any) {
      console.error('Error loading law packs:', error);
      toast({
        title: "Error loading law packs",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleLawPackStatus = async (packId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('law_packs')
        .update({ is_active: !currentStatus })
        .eq('id', packId);

      if (error) throw error;

      // Update local state
      setLawPacks(packs => 
        packs.map(pack => 
          pack.id === packId 
            ? { ...pack, is_active: !currentStatus }
            : pack
        )
      );

      toast({
        title: `Law pack ${!currentStatus ? 'activated' : 'deactivated'}`,
        description: `${lawPacks.find(p => p.id === packId)?.name} has been ${!currentStatus ? 'activated' : 'deactivated'}.`
      });
    } catch (error: any) {
      toast({
        title: "Error updating law pack",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="container mx-auto max-w-6xl">
        <div className="mb-8">
          <Breadcrumb className="mb-4">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/dashboard" className="flex items-center gap-2">
                    <Home className="h-4 w-4" />
                    Dashboard
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator>
                <ChevronRight className="h-4 w-4" />
              </BreadcrumbSeparator>
              <BreadcrumbItem>
                <BreadcrumbPage>Law Packs</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="heading-section mb-2">Law Packs</h1>
              <p className="text-muted-foreground">Manage compliance law packs with versioning and updates</p>
            </div>
            <div className="flex gap-3">
              <Button 
                onClick={() => setQuickCreateOpen(true)}
                variant="outline"
                className="border-primary/50 hover:bg-primary/10"
              >
                <Flag className="h-4 w-4 mr-2" />
                Quick Create
              </Button>
              <Link to="/law-pack-import">
                <Button className="cta-primary">
                  <Plus className="h-4 w-4 mr-2" />
                  Import Law Pack
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Update Notifications — only visible when there are real pending updates */}
        {pendingUpdatesCount > 0 && (
          <Card className="glass-panel border-0 mb-6 border-l-4 border-l-orange-500">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <AlertCircle className="h-5 w-5 text-orange-500 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-medium mb-2">
                    {pendingUpdatesCount} law pack update{pendingUpdatesCount !== 1 ? 's' : ''} available
                  </h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    Review and apply the latest regulatory updates to your law packs.
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" className="cta-primary" onClick={() => setUpdatesDialogOpen(true)}>
                      Review Updates
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {isMasterAdmin && (
          <div className="mb-6">
            <Button size="sm" variant="outline" asChild>
              <Link to="/law-packs/updates">Publish a law pack update</Link>
            </Button>
          </div>
        )}

        {/* Law Packs Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="glass-panel border-0">
                <CardContent className="pt-6">
                  <div className="animate-pulse space-y-4">
                    <div className="h-4 bg-muted rounded w-3/4"></div>
                    <div className="h-3 bg-muted rounded w-1/2"></div>
                    <div className="space-y-2">
                      <div className="h-3 bg-muted rounded"></div>
                      <div className="h-3 bg-muted rounded"></div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {lawPacks.map((pack) => (
              <Card key={pack.id} className="glass-panel border-0">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{pack.name}</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">{pack.region}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost" 
                        size="sm"
                        onClick={() => toggleLawPackStatus(pack.id, pack.is_active)}
                        className="h-8 w-8 p-0"
                      >
                        {pack.is_active ? (
                          <ToggleRight className="h-5 w-5 text-green-500" />
                        ) : (
                          <ToggleLeft className="h-5 w-5 text-muted-foreground" />
                        )}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Version</span>
                      <span className="text-sm font-medium">{pack.version}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Status</span>
                      <Badge variant={pack.is_active ? 'default' : 'secondary'}>
                        {pack.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Sites Using</span>
                      <span className="text-sm font-medium">{pack.sites_count || 0}</span>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Last Updated</span>
                      <span className="text-sm font-medium">
                        {new Date(pack.updated_at).toLocaleDateString()}
                      </span>
                    </div>
                    
                    {/* External Resources Indicator */}
                    {(pack.external_links?.length > 0 || pack.official_website) && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">External Resources</span>
                        <div className="flex items-center gap-1">
                          {pack.official_website && <Globe className="h-3 w-3 text-blue-500" />}
                          {pack.update_feed_url && <Rss className="h-3 w-3 text-orange-500" />}
                          {pack.external_links?.length > 0 && (
                            <Badge variant="outline" className="text-xs">
                              {pack.external_links.length} links
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <div className="flex gap-2 pt-2">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button size="sm" variant="outline" className="glass-button flex-1">
                            <Settings className="h-4 w-4 mr-2" />
                            Configure
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="glass-panel border-0 max-w-4xl max-h-[80vh] overflow-y-auto">
                          <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                              <Building2 className="h-5 w-5" />
                              {pack.name}
                            </DialogTitle>
                            <DialogDescription>
                              {pack.description || `Law pack for ${pack.region} - Version ${pack.version}`}
                            </DialogDescription>
                          </DialogHeader>
                          
                          <div className="space-y-6">
                            {/* Basic Information */}
                            <div>
                              <h4 className="font-medium mb-3 flex items-center gap-2">
                                <FileText className="h-4 w-4" />
                                Basic Information
                              </h4>
                              <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                  <span className="text-muted-foreground">Region:</span>
                                  <p className="font-medium">{pack.region}</p>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Version:</span>
                                  <p className="font-medium">{pack.version}</p>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Status:</span>
                                  <Badge variant={pack.is_active ? 'default' : 'secondary'}>
                                    {pack.is_active ? 'Active' : 'Inactive'}
                                  </Badge>
                                </div>
                                <div>
                                  <span className="text-muted-foreground">Sites Using:</span>
                                  <p className="font-medium">{pack.sites_count || 0}</p>
                                </div>
                                {pack.regulatory_authority && (
                                  <div className="col-span-2">
                                    <span className="text-muted-foreground">Regulatory Authority:</span>
                                    <p className="font-medium">{pack.regulatory_authority}</p>
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Official Website */}
                            {pack.official_website && (
                              <div>
                                <h4 className="font-medium mb-3 flex items-center gap-2">
                                  <Globe className="h-4 w-4" />
                                  Official Website
                                </h4>
                                <Button 
                                  variant="outline" 
                                  className="glass-button" 
                                  onClick={() => window.open(pack.official_website, '_blank')}
                                >
                                  <ExternalLink className="h-4 w-4 mr-2" />
                                  Visit Official Site
                                </Button>
                              </div>
                            )}

                            {/* External Links */}
                            {pack.external_links && pack.external_links.length > 0 && (
                              <div>
                                <h4 className="font-medium mb-3 flex items-center gap-2">
                                  <ExternalLink className="h-4 w-4" />
                                  External Resources
                                </h4>
                                <div className="space-y-3">
                                  {pack.external_links.map((link, index) => (
                                    <Card key={index} className="glass-panel border-0">
                                      <CardContent className="p-4">
                                        <div className="flex items-start justify-between">
                                          <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                              <h5 className="font-medium">{link.title}</h5>
                                              <Badge 
                                                variant="outline" 
                                                className={`text-xs ${
                                                  link.type === 'regulation' ? 'border-red-200 text-red-700' :
                                                  link.type === 'guidance' ? 'border-blue-200 text-blue-700' :
                                                  'border-green-200 text-green-700'
                                                }`}
                                              >
                                                {link.type}
                                              </Badge>
                                            </div>
                                            {link.description && (
                                              <p className="text-sm text-muted-foreground">{link.description}</p>
                                            )}
                                          </div>
                                          <Button 
                                            size="sm" 
                                            variant="outline" 
                                            onClick={() => window.open(link.url, '_blank')}
                                          >
                                            <ExternalLink className="h-3 w-3" />
                                          </Button>
                                        </div>
                                      </CardContent>
                                    </Card>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Source Documents */}
                            {pack.source_documents && pack.source_documents.length > 0 && (
                              <div>
                                <h4 className="font-medium mb-3 flex items-center gap-2">
                                  <FileText className="h-4 w-4" />
                                  Source Documents
                                </h4>
                                <div className="space-y-2">
                                  {pack.source_documents.map((doc, index) => (
                                    <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                                      <div className="flex items-center gap-3">
                                        <FileText className="h-4 w-4 text-muted-foreground" />
                                        <div>
                                          <p className="font-medium text-sm">{doc.title}</p>
                                          {doc.size && (
                                            <p className="text-xs text-muted-foreground">{doc.size}</p>
                                          )}
                                        </div>
                                      </div>
                                      <Button 
                                        size="sm" 
                                        variant="outline"
                                        onClick={() => window.open(doc.url, '_blank')}
                                      >
                                        <Download className="h-3 w-3 mr-1" />
                                        Download
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Update Feed */}
                            {pack.update_feed_url && (
                              <div>
                                <h4 className="font-medium mb-3 flex items-center gap-2">
                                  <Rss className="h-4 w-4" />
                                  Update Feed
                                </h4>
                                <p className="text-sm text-muted-foreground mb-3">
                                  Subscribe to get the latest regulatory updates for {pack.region}.
                                </p>
                                <Button 
                                  variant="outline" 
                                  className="glass-button" 
                                  onClick={() => window.open(pack.update_feed_url, '_blank')}
                                >
                                  <Rss className="h-4 w-4 mr-2" />
                                  Subscribe to Updates
                                </Button>
                              </div>
                            )}
                          </div>
                        </DialogContent>
                      </Dialog>
                      <Button size="sm" variant="outline" className="glass-button">
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {!loading && lawPacks.length === 0 && (
          <Card className="glass-panel border-0">
            <CardContent className="pt-6 text-center">
              <p className="text-muted-foreground">No law packs found. Import your first law pack to get started.</p>
            </CardContent>
          </Card>
        )}

        {/* Legal Disclaimer */}
        <Card className="glass-panel border-0 mt-8">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                <strong>Legal Disclaimer:</strong> Law packs provide guidance only. Operators remain fully responsible for compliance with applicable food safety regulations.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Law Pack Updates Dialog */}
      <LawPackUpdatesDialog
        open={updatesDialogOpen}
        onOpenChange={setUpdatesDialogOpen}
        onApplied={fetchPendingUpdatesCount}
      />

      {/* Quick Create Law Pack Wizard */}
      <QuickCreateLawPackWizard
        open={quickCreateOpen}
        onOpenChange={setQuickCreateOpen}
        onSuccess={fetchLawPacks}
      />
    </div>
  );
};

export default LawPacks;